import { useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DocumentFiltersPopup } from './doc-filters';
import { DocSortPopup } from './doc-sort';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { DeleteDocumentsDialog } from '../../../../docs/components/delete-documents-dialog';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteMatterDocs } from '@/api/mutations/useDeleteMatterDocs';
import { useStoreSubsetDocIds } from '@/api/mutations/useStoreSubsetDocIds';
import { Issue } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { DocChronologyDropdown } from '@/new/components/doc-chronology-dropdown';
import { FilterPills } from '@/new/components/filter-pills';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetChip } from '@/new/components/subset-chip';
import { SubsetDropdown } from '@/new/components/subset-dropdown';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { DocFilters } from '@/types';
import { FilterPill } from '@/utils/buildFilterPills';

interface DocumentEditorToolbarProps {
  matterId: string;
  subsetId: string;
  isAllDocuments?: boolean;
  matterIssues?: Issue[];
  isMatterDraft?: boolean;
  isSampleMatter?: boolean;
  openDownloadModal: () => void;
  openDownloadZipModal: () => void;
  openDownloadAllZipModal: () => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  clearSearch: () => void;
  processingNL?: boolean;
  currentPage: number;
  totalPages?: number;
  hasMore?: boolean;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  hasActiveFilters: boolean;
  totalCount?: number;
  totalUnfilteredCount?: number;
  filterPills: FilterPill[];
  onClearAllFilters: () => void;
  filters: DocFilters;
  setFilters: (filters: DocFilters) => void;
  allDocumentTypes: string[];
  docIssues: string[];
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'ASC' | 'DESC';
  setSortOrder: (value: 'ASC' | 'DESC') => void;
  clearSort: () => void;
  // Document selection
  selectedDocIds: Set<string>;
  allDocsOnPageChecked: boolean;
  toggleAllDocsHandler: () => Promise<void>;
  deselectAllDocsFromDb: () => void;
  isAllDocIdsLoading: boolean;
  isLoadingDocs: boolean;
}

export function DocTableHeader({
  matterId,
  subsetId,
  isAllDocuments,
  matterIssues,
  isMatterDraft,
  isSampleMatter,
  openDownloadModal,
  openDownloadZipModal,
  openDownloadAllZipModal,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  hasMore,
  prevPage,
  nextPage,
  goToPage,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  processingNL,
  totalCount,
  totalUnfilteredCount,
  filterPills,
  onClearAllFilters,
  filters,
  setFilters,
  allDocumentTypes,
  docIssues,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  clearSort,
  // Document selection
  selectedDocIds,
  allDocsOnPageChecked,
  toggleAllDocsHandler,
  deselectAllDocsFromDb,
  isAllDocIdsLoading,
  isLoadingDocs,
}: DocumentEditorToolbarProps) {
  const { getProcessingState } = useMatterProcessingStore();
  const processingState = getProcessingState(matterId);
  const isProcessing = processingState?.hasProcessingRuns ?? false;

  const navigate = useNavigate();
  const storeDocIds = useStoreSubsetDocIds();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deleteDocs, isLoading: isDeleting } = useDeleteMatterDocs(matterId, () => {
    toast.success('Deleting documents...');
    setDeleteDialogOpen(false);
    deselectAllDocsFromDb();
  });

  return (
    <PageContainer className="border-b pb-3">
      <PageHeader>
        <div className="flex items-center gap-4">
          <PageTitle text="Documents" />
          <SubsetChip subsetId={subsetId} className="mt-0.5" />
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex justify-end gap-2">
            {isAllDocuments !== undefined &&
              (isAllDocuments ? (
                !isMatterDraft && (
                  <PermissionGate permission={Permission.DocumentUpload} uiMode="disabled">
                    {isSampleMatter ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button variant="primary" disabled>
                              <Icon className="lucide--upload" />
                              Upload Documents
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>You can't upload documents to a sample Matter.</TooltipContent>
                      </Tooltip>
                    ) : matterIssues ? (
                      <Button asChild variant="primary">
                        <Link to={`/matters/${matterId}/docs/upload`}>
                          <Icon className="lucide--upload" />
                          Upload Documents
                        </Link>
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 px-2 text-xs text-gray-600">
                        <Icon className="lucide--info" />
                        <p>
                          <Link className="text-blue-500" to={`/matters/${matterId}/settings/issues`}>
                            Add Matter issues
                          </Link>
                          <span> to upload more documents</span>
                        </p>
                      </div>
                    )}
                  </PermissionGate>
                )
              ) : (
                <Button asChild variant="primary">
                  <Link
                    to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                    onClick={() => {
                      trackCustomEvent(TrackingEventsV2.DocumentClickAdd);
                    }}
                  >
                    <Icon className="lucide--diff" />
                    Edit Documents in Subset
                  </Link>
                </Button>
              ))}

            <SubsetDropdown
              matterId={matterId}
              subsetId={subsetId}
              disabled={selectedDocIds.size === 0}
              triggerText="Subset"
              isLoading={storeDocIds.isLoading}
              onCreateNew={async () => {
                trackCustomEvent(TrackingEventsV2.SubsetOpenCreateNew, { matterId });
                try {
                  const result = await storeDocIds.mutateAsync({
                    matterId,
                    docIds: Array.from(selectedDocIds),
                  });
                  navigate(`/matters/${matterId}/subsets/new?factsQueryKey=${result.queryKey}`);
                } catch {
                  toast.error('Failed to prepare documents for subset creation');
                }
              }}
              getDocIds={async () => Array.from(selectedDocIds)}
            />

            <DocChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedDocIds={selectedDocIds}
              triggerText="Document Chronology"
              createDescription="Create a new document chronology from the selected documents"
              addDescription="Select an existing document chronology to add documents to"
            />

            <PermissionGate permission={Permission.DocumentDownload} uiMode="disabled">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={!getProcessingState(matterId)?.hasCompletedRuns}>
                    <Icon className="lucide--download" />
                    <span className="font-medium">Download</span>
                    <Icon className="lucide--chevron-down" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {hasActiveFilters ? (
                    <>
                      <DropdownMenuItem onClick={openDownloadZipModal}>Download Filtered Zip</DropdownMenuItem>
                      <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                      <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                      <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </PermissionGate>

            {isAllDocuments && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={'ghost'}>
                    <Icon className="lucide--more-vertical -ml-1.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <PermissionGate
                    permission={Permission.DocumentDelete}
                    disabledMessage="You do not have permission to delete documents"
                    uiMode="disabled"
                  >
                    <DropdownMenuItem
                      disabled={selectedDocIds.size === 0 || isProcessing}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Icon className="lucide--trash-2 text-red-500" />
                      Delete {selectedDocIds.size > 0 ? `${selectedDocIds.size} ` : ''}Document
                      {selectedDocIds.size === 1 ? '' : 's'}
                    </DropdownMenuItem>
                  </PermissionGate>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </PageHeader>

      <div className="flex gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="documents"
        />

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--sort-asc" />
              <span>Sort</span>
            </Button>
          </PopoverTrigger>

          <PopoverContent side="left" className="mt-12 min-w-md" sideOffset={8}>
            <div className="-mx-4 -mt-3 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">
              Document Sorting
            </div>

            <div className="mt-4 flex flex-col gap-4">
              <DocSortPopup
                activeSortBy={sortBy}
                activeSortOrder={sortOrder}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                hasActiveSearch={!!globalFilter && globalFilter.trim() !== ''}
              />
            </div>

            <div className="mt-4 flex w-full justify-end">
              <Button onClick={clearSort}>
                <span>Clear Sorting</span>
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--funnel-plus" />
              <span>Add Filters</span>
            </Button>
          </PopoverTrigger>
          <FilterPopoverContent clearAllFilters={onClearAllFilters}>
            <DocumentFiltersPopup
              matterId={matterId}
              activeFilters={filters}
              setFilters={setFilters}
              allDocumentTypes={allDocumentTypes}
              docIssues={docIssues}
            />
          </FilterPopoverContent>
        </Popover>
      </div>

      <div>
        <FilterPills pills={filterPills} onClearAll={onClearAllFilters} />
      </div>

      <div className="flex items-center justify-between pt-4 pb-2">
        <div className="flex items-center gap-3 pl-1 text-xs text-gray-500">
          <Checkbox
            checked={allDocsOnPageChecked}
            onCheckedChange={toggleAllDocsHandler}
            disabled={isLoadingDocs || isAllDocIdsLoading}
          />
          <p>
            <strong>{selectedDocIds.size.toLocaleString()}</strong> Processed Docs selected
          </p>

          <Button size="sm" disabled={selectedDocIds.size === 0} onClick={deselectAllDocsFromDb}>
            Clear All ({selectedDocIds.size}) Selected Docs
          </Button>
        </div>

        <Pagination
          canGetPrevPage={currentPage > 0}
          canGetNextPage={totalPages !== undefined ? currentPage < totalPages - 1 : (hasMore ?? false)}
          prevPage={prevPage}
          nextPage={nextPage}
          currentPage={currentPage}
          noOfPages={totalPages}
          goToPage={goToPage}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          totalCount={totalUnfilteredCount}
          filteredCount={totalCount}
          entityName="documents/folders"
        />
      </div>

      <DeleteDocumentsDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => {
          if (selectedDocIds.size > 0) {
            deleteDocs(Array.from(selectedDocIds));
          }
        }}
        isDeleting={isDeleting}
        selectedCount={selectedDocIds.size}
      />
    </PageContainer>
  );
}
